import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getStoredUser, hydrateAuthUser, logout } from '../../api/client';
import { getRoleHomePath, getRoleLabel, getTopNavLinks } from '../../auth/roles';
import logo from '../../logo.svg';

function SidebarIcon({ name, width, height }) {
  const props = {
    className: 'sidebar-icon',
    viewBox: '0 0 24 24',
    width,
    height,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  switch (name) {
    case 'dashboard':
      return (
        <svg {...props}>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.8" />
          <rect x="13.5" y="3.5" width="7" height="4.5" rx="1.8" />
          <rect x="13.5" y="10.5" width="7" height="10" rx="1.8" />
          <rect x="3.5" y="12.5" width="7" height="8" rx="1.8" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M8 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
          <path d="M16.5 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
          <path d="M3.5 19.5a4.8 4.8 0 0 1 9 0" />
          <path d="M13.5 19.5a4 4 0 0 1 7 0" />
        </svg>
      );
    case 'create':
      return (
        <svg {...props}>
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      );
    case 'audit':
      return (
        <svg {...props}>
          <path d="M12 3.5 5.5 6v5.2c0 4.2 2.6 7.8 6.5 9.3 3.9-1.5 6.5-5.1 6.5-9.3V6L12 3.5Z" />
          <path d="m9.2 12.2 1.9 1.9 3.8-4.4" />
        </svg>
      );
    case 'approvals':
      return (
        <svg {...props}>
          <path d="M6 4.5h12v15H6z" />
          <path d="m8.8 12 2 2 4.5-5" />
        </svg>
      );
    case 'mastery':
      return (
        <svg {...props}>
          <path d="M4.5 18.5h15" />
          <path d="M7.5 15.5v-4" />
          <path d="M12 15.5V8" />
          <path d="M16.5 15.5V5.5" />
        </svg>
      );
    case 'students':
      return (
        <svg {...props}>
          <path d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path d="M16 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
          <path d="M3.5 19a4.5 4.5 0 0 1 9 0" />
          <path d="M13.5 19a4 4 0 0 1 7 0" />
        </svg>
      );
    case 'practice':
      return (
        <svg {...props}>
          <path d="M5.5 7.5h13" />
          <path d="M7.5 12h9" />
          <path d="M9.5 16.5h5" />
          <path d="M4.5 5.5h15v13h-15z" />
        </svg>
      );
    case 'children':
      return (
        <svg {...props}>
          <path d="M7.5 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
          <path d="M15.5 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path d="M3.5 19a4 4 0 0 1 8 0" />
          <path d="M11.5 19a4.8 4.8 0 0 1 9 0" />
        </svg>
      );
    case 'help':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M9.8 9.5a2.4 2.4 0 0 1 4.6 1c0 1.6-1.5 2.1-2.1 3.1" />
          <path d="M12 17h.01" />
        </svg>
      );
    case 'account':
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
        </svg>
      );
    case 'chevron-left':
      return (
        <svg {...props}>
          <path d="m14.5 6.5-5 5.5 5 5.5" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...props}>
          <path d="m9.5 6.5 5 5.5-5 5.5" />
        </svg>
      );
    case 'logout':
      return (
        <svg {...props}>
          <path d="M9.5 4.5h-2A2.5 2.5 0 0 0 5 7v10a2.5 2.5 0 0 0 2.5 2.5h2" />
          <path d="M14 8.5 19 12l-5 3.5" />
          <path d="M10 12h9" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...props}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function getNavIconName(link) {
  if (link.to === '/dashboard') {
    return 'dashboard';
  }
  if (link.to === '/admin/users') {
    return 'users';
  }
  if (link.to === '/admin/users/create') {
    return 'create';
  }
  if (link.to === '/cohort') {
    return 'users';
  }
  if (link.to === '/admin/audit') {
    return 'audit';
  }
  if (link.to === '/admin/question-approvals') {
    return 'approvals';
  }
  if (link.to === '/practice') {
    return 'practice';
  }
  if (link.to === '/analytics/mastery') {
    return 'mastery';
  }
  if (link.to === '/tutor/students') {
    return 'students';
  }
  if (link.to === '/parent/students') {
    return 'children';
  }
  if (link.to === '/account') {
    return 'account';
  }
  if (link.to === '/beta-help' || String(link.to || '').startsWith('/beta-help#')) {
    return 'help';
  }
  return 'dashboard';
}

function TopNav({ isCollapsed = false, onToggleCollapse, onReportIssue = null, onNavigate = null, mobile = false }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser);

  useEffect(() => {
    let active = true;
    const refreshUser = async () => {
      try {
        const result = await hydrateAuthUser();
        if (active && result?.user) setUser(result.user);
      } catch (_error) {
        // Keep the last known session state during transient refresh failures.
      }
    };
    const interval = window.setInterval(refreshUser, 30000);
    window.addEventListener('focus', refreshUser);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshUser);
    };
  }, []);

  const role = user?.role || null;
  const links = getTopNavLinks(role);
  const isGuardianBlockedStudent = role === 'STUDENT'
    && user?.account_state === 'guardian_pending';
  const roleLabel = getRoleLabel(role);
  const displayName = user?.display_name
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    || user?.email
    || 'Sumrise account';
  const userInitial = (displayName || roleLabel).trim().charAt(0).toUpperCase() || 'S';
  const toggleLabel = isCollapsed ? 'Expand sidebar' : 'Collapse sidebar';
  const accountLabel = `${displayName} · ${roleLabel}`;
  const handleBrandClick = () => {
    if (isCollapsed) {
      onToggleCollapse?.();
      return;
    }
    if (!isGuardianBlockedStudent) {
      navigate(getRoleHomePath(role));
      onNavigate?.();
    }
  };

  return (
    <aside className={`app-sidebar${isCollapsed ? ' is-collapsed' : ''}${mobile ? ' is-mobile-drawer' : ''}`}>
      <div className="sidebar-top">
        <div className="sidebar-brand-row">
          <button
            type="button"
            className="sidebar-brand"
            onClick={handleBrandClick}
            disabled={isGuardianBlockedStudent && !isCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : (isGuardianBlockedStudent ? 'Sumrise' : 'Go to dashboard')}
            title={isCollapsed ? 'Expand sidebar' : undefined}
          >
            <span className="sidebar-icon-box sidebar-brand-mark" aria-hidden="true">
              <img src={logo} alt="" className="sidebar-brand-logo" />
            </span>
            {!isCollapsed ? (
              <span className="sidebar-brand-text">
                <strong>Sumrise</strong>
                <span>{roleLabel} workspace</span>
              </span>
            ) : null}
          </button>
          {!isCollapsed && !mobile ? (
            <button
              type="button"
              className="sidebar-toggle sidebar-icon-box"
              onClick={onToggleCollapse}
              aria-label={toggleLabel}
              title={toggleLabel}
            >
              <SidebarIcon name="chevron-left" />
            </button>
          ) : null}
        </div>
      </div>

      <nav aria-label="Primary" className="sidebar-nav">
        {links.map((link) => {
          const content = (
            <>
              <span className="sidebar-link-icon sidebar-icon-box" aria-hidden="true">
                <SidebarIcon name={getNavIconName(link)} />
              </span>
              {!isCollapsed ? <span className="sidebar-link-label">{link.label}</span> : null}
            </>
          );

          return isGuardianBlockedStudent && link.to !== '/account' ? (
            <span
              key={link.to}
              className="sidebar-link is-disabled"
              aria-disabled="true"
              aria-label={isCollapsed ? `${link.label} unavailable` : undefined}
              title={isCollapsed ? `${link.label} unavailable` : undefined}
            >
              {content}
            </span>
          ) : (
            <NavLink
              key={link.to}
              className={({ isActive }) => `sidebar-link${isActive ? ' is-active' : ''}`}
              to={link.to}
              onClick={onNavigate}
              aria-label={isCollapsed ? link.label : undefined}
              title={isCollapsed ? link.label : undefined}
            >
              {content}
            </NavLink>
          );
        })}
        {onReportIssue ? (
          <button
            type="button"
            className="sidebar-link sidebar-link-button"
            onClick={onReportIssue}
            aria-label={isCollapsed ? 'Report issue' : undefined}
            title={isCollapsed ? 'Report issue' : undefined}
          >
            <span className="sidebar-link-icon sidebar-icon-box" aria-hidden="true">
              <SidebarIcon name="help" />
            </span>
            {!isCollapsed ? <span className="sidebar-link-label">Report issue</span> : null}
          </button>
        ) : null}
      </nav>

      <div className="sidebar-footer">
        <div
          className="sidebar-account"
          aria-label={accountLabel}
          title={user?.email || (isCollapsed ? accountLabel : undefined)}
        >
          <span className="sidebar-icon-box sidebar-user-avatar" aria-hidden="true">{userInitial}</span>
          {!isCollapsed ? (
            <div className="sidebar-user-meta">
              <strong>{displayName}</strong>
              <span>{roleLabel}</span>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-secondary sidebar-logout"
          onClick={() => logout()}
          aria-label="Logout"
          title={isCollapsed ? 'Logout' : undefined}
        >
          <span className="sidebar-link-icon sidebar-icon-box" aria-hidden="true">
            <SidebarIcon name="logout" />
          </span>
          {!isCollapsed ? <span>Logout</span> : null}
        </button>
      </div>
    </aside>
  );
}

export default TopNav;
export { SidebarIcon };
